import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../../AppStores";
import type {View} from "../../../../../data/models/View";
import type {AppState} from "../../../../../data/state/AppState";
import {XyiconFeature} from "../../../../../generated/api/base";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import type {Markup3D} from "../../../../modules/space/spaceeditor/logic3d/elements3d/markups/abstract/Markup3D";
import {isMarkupFilteredOutByColor} from "../../../../modules/space/spaceeditor/logic3d/elements3d/markups/abstract/MarkupUtils";
import type {SpaceItemType} from "../../../../modules/space/spaceeditor/logic3d/managers/spaceitems/ItemManager";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import {getFeatureBySpaceItemType} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {ILayerSection} from "../../../../modules/space/spaceeditor/ui/viewbar/LayerView";
import {CheckboxInputV5} from "../../../details/datatypes/CheckboxInputV5";
import {HideAndLockV5} from "../../../details/HideAndLockV5";
import type {LayerType} from "../../../../../data/models/ViewUtils";
import {LayerViewStyled} from "./LayerView.styled";

interface ILayerViewProps {
	readonly view: View;
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly showNumbers: boolean;
	readonly searchString: string;
	readonly activeSection: SpaceItemType;
	readonly appState?: AppState;
}

@withAppStores("appState")
@observer
export class LayerViewV5 extends React.Component<ILayerViewProps> {
	private readonly _tooltipForNumbers = "Filters are applied";

	private onLayerSettingsChange(type: LayerType) {
		if (this.props.view === this.selectedSpaceEditorView) {
			if (type === "lock" || this.props.activeSection === "markup") {
				this.props.spaceViewRenderer.spaceItemController.onLayerSettingsModified();
			} else {
				// if we modify the "hide" settings, the filter gets updated anyway, because they're connected
				// except for markups...
			}
		}
	}

	private get selectedSpaceEditorView() {
		return this.props.appState.actions.getSelectedView(XyiconFeature.SpaceEditor);
	}

	private get feature() {
		return getFeatureBySpaceItemType(this.props.activeSection);
	}

	private get itemManager() {
		return this.props.spaceViewRenderer.getItemManager(this.feature);
	}

	private getFilteredItemsCountForSection(section: ILayerSection) {
		const hiddenMarkupColors = new Set<string>(this.selectedSpaceEditorView.spaceEditorViewSettings.layers.hiddenMarkupColors);

		return this.itemManager.items.array.filter((spaceItem) => {
			if (spaceItem.spaceItemType === "markup") {
				const markup3D = spaceItem as Markup3D;

				return (
					!markup3D.isTemp &&
					!markup3D.layerSettings.isHidden &&
					!isMarkupFilteredOutByColor(hiddenMarkupColors, markup3D) &&
					markup3D.typeName.toLowerCase() === section.title.replaceAll(" ", "").toLowerCase()
				);
			}
			return !spaceItem.layerSettings.isHidden && spaceItem.typeName === section.title;
		}).length;
	}

	private getAllItemsCountForSection(section: ILayerSection) {
		return this.itemManager.items.array.filter((spaceItem) => {
			if (spaceItem.spaceItemType === "markup") {
				return !(spaceItem as Markup3D).isTemp && spaceItem.typeName.toLowerCase() === section.title.replaceAll(" ", "").toLowerCase();
			}
			return spaceItem.typeName === section.title;
		}).length;
	}

	private getHideAndLockSectionElement(section: ILayerSection, index: number) {
		let title = section.title;
		let itemCount = "";

		if (this.props.showNumbers) {
			const filteredItemsCount = this.getFilteredItemsCountForSection(section);
			const allItemsCount = this.getAllItemsCountForSection(section);

			itemCount = `(${filteredItemsCount}/${allItemsCount})`;
		}

		if (this.props.activeSection === "markup") {
			title = StringUtils.pascalToSpacedName(title);
		}

		return (
			<div
				className="field hbox alignCenter"
				key={index}
			>
				<HideAndLockV5
					isActive={section.isPositionLocked}
					type="lock"
					onChange={(active: boolean) => {
						section.isPositionLocked = active;
						this.onLayerSettingsChange("lock");
					}}
				/>
				<div className="title-container">
					<CheckboxInputV5
						onChange={(active: boolean) => {
							section.isHidden = !active;
							this.onLayerSettingsChange("hide");
						}}
						value={!section.isHidden}
					/>

					<div
						className="layer-title"
						title={title}
					>
						{title}
					</div>

					<span className="itemCount">{itemCount}</span>
				</div>
			</div>
		);
	}

	private getSumOfItems() {
		if (this.itemManager.feature === XyiconFeature.Markup) {
			return this.itemManager.items.array.filter((item) => !(item as Markup3D).isTemp).length;
		} else {
			return this.itemManager.items.array.length;
		}
	}

	private getSumOfFilteredItems() {
		const hiddenMarkupColors = new Set<string>(this.selectedSpaceEditorView.spaceEditorViewSettings.layers.hiddenMarkupColors);

		return this.itemManager.items.array.filter(
			(spaceItem) =>
				!spaceItem.layerSettings?.isHidden &&
				(spaceItem.spaceItemType !== "markup" ||
					(!isMarkupFilteredOutByColor(hiddenMarkupColors, spaceItem as Markup3D) && !(spaceItem as Markup3D).isTemp)),
		).length;
	}

	private get layerSettings() {
		return this.props.view.spaceEditorViewSettings.layers[this.props.activeSection];
	}

	private renderTogglers() {
		const layerSettings = this.layerSettings;

		const layerColumns = {
			included: Object.values(layerSettings.included),
		};

		const filterBySearch = (layerSettings: ILayerSection) => StringUtils.containsIgnoreCase(`${layerSettings.title}`, this.props.searchString);

		const fieldElements: React.ReactNode[] = [];
		const sortedIncludedSections = layerColumns.included
			.filter(filterBySearch)
			.toSorted((a: ILayerSection, b: ILayerSection) => StringUtils.sortIgnoreCase(a.title, b.title));

		fieldElements.push(...sortedIncludedSections.map((section: ILayerSection, index: number) => this.getHideAndLockSectionElement(section, index)));

		const allTitleBase = "All";
		let allTitle = allTitleBase;

		const sumOfFilteredItems = this.getSumOfFilteredItems();
		const sumOfItems = this.getSumOfItems();
		let tooltip = "";

		if (this.props.showNumbers) {
			allTitle = `${allTitle} (${sumOfFilteredItems}/${sumOfItems})`;

			if (sumOfFilteredItems !== sumOfItems) {
				tooltip = this._tooltipForNumbers;
			}
		}

		return (
			<div className="fieldContainer">
				{StringUtils.containsIgnoreCase(allTitleBase, this.props.searchString) && (
					<div className="field hbox alignCenter">
						<HideAndLockV5
							isActive={sortedIncludedSections.every((layerSettings: ILayerSection) => layerSettings.isPositionLocked)}
							type="lock"
							onChange={(active: boolean) => {
								for (const key in layerSettings.included) {
									layerSettings.included[key].isPositionLocked = active;
								}
								this.onLayerSettingsChange("lock");
							}}
							isItForModifyAll={true}
						/>
						<div className="title-container">
							<CheckboxInputV5
								onChange={(active: boolean) => {
									for (const key in layerSettings.included) {
										layerSettings.included[key].isHidden = !active;
									}
									this.onLayerSettingsChange("hide");
								}}
								value={sortedIncludedSections.every((layerSettings: ILayerSection) => !layerSettings.isHidden)}
							/>
							<div
								className="layer-title"
								title={tooltip}
							>
								{allTitle}
							</div>
						</div>
					</div>
				)}
				<div
					className="fields"
					data-cy="hide-and-lock-by-type"
				>
					{fieldElements}
				</div>
			</div>
		);
	}

	private forceUpdateArrow = () => {
		this.forceUpdate();
	};

	public override componentDidMount() {
		const {spaceItemController} = this.props.spaceViewRenderer;

		spaceItemController.signals.filterUpdated.add(this.forceUpdateArrow);

		const feature = this.feature;

		const itemManager = this.props.spaceViewRenderer.getItemManager(feature);

		itemManager.signals.itemsAdd.add(this.forceUpdateArrow);
		itemManager.signals.itemsRemove.add(this.forceUpdateArrow);
	}

	public override componentWillUnmount() {
		const {spaceItemController} = this.props.spaceViewRenderer;

		spaceItemController.signals.filterUpdated.remove(this.forceUpdateArrow);

		const itemManager = this.props.spaceViewRenderer.getItemManager(this.feature);

		itemManager.signals.itemsAdd.remove(this.forceUpdateArrow);
		itemManager.signals.itemsRemove.remove(this.forceUpdateArrow);
	}

	public override render() {
		return <LayerViewStyled>{this.renderTogglers()}</LayerViewStyled>;
	}
}
